import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSolanaClusterParam } from '@/utils/solanaExplorer';
import {
  Plus,
  TrendingUp,
  TrendingDown,
  Shield,
  Clock,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { AddMetricModal } from './AddMetricModal';
import { PhysicalMetric, MetricType, UpdaterType } from '../../../../api/pets';
import {
  usePhysicalMetrics,
  useCreatePhysicalMetric,
} from '../../../../hooks/usePetRecords';
import { useNotifications } from '../../context/NotificationsContext';
import { usePets } from '../../context/PetsContext';

interface PhysicalMetricsProps {
  petId: string;
}

export function PhysicalMetrics({ petId }: PhysicalMetricsProps) {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);

  const {
    data: metrics = [],
    isLoading,
    error,
    isFetching,
  } = usePhysicalMetrics(petId);
  const createMetricMutation = useCreatePhysicalMetric(petId);
  const { addNotification } = useNotifications();
  const { pets } = usePets();
  const petName = pets.find((p) => p.id === petId)?.name ?? 'Your pet';

  const weightHistory = metrics
    .filter((m) => m.type === 'weight')
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const latestWeight = weightHistory[0];
  const previousWeight = weightHistory[1];
  const weightChange =
    previousWeight && latestWeight
      ? latestWeight.value - previousWeight.value
      : 0;

  const latestHeight = metrics.find((m) => m.type === 'height');
  const latestBodyCondition = metrics.find((m) => m.type === 'bodyCondition');

  const getUpdaterBadge = (metric: PhysicalMetric) => {
    const colors = {
      owner: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
      vet: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
      groomer:
        'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    };

    return (
      <div className="flex items-center gap-2">
        <span
          className={`px-2 py-1 rounded text-xs ${colors[metric.updatedByType]}`}
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {metric.updatedByType === 'owner'
            ? '👤 ' + t('features.healthRecords.ownerBadge')
            : metric.updatedByType === 'vet'
              ? '🏥 ' + t('features.healthRecords.vetBadge')
              : '✂️ ' + t('features.healthRecords.groomerBadge')}
        </span>
        {metric.verified && (
          <span
            className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <Shield className="w-3 h-3" />
            {t('features.healthRecords.verifiedBadge')}
          </span>
        )}
      </div>
    );
  };

  const handleAddMetric = async (newMetricData: {
    type: string;
    value: number;
    unit: string;
    date: string;
    notes?: string;
  }) => {
    try {
      await createMetricMutation.mutateAsync({
        type: newMetricData.type as MetricType,
        value: newMetricData.value,
        unit: newMetricData.unit,
        date: newMetricData.date,
        updatedBy: 'Current User', // TODO: Get actual user name
        updatedByType: 'owner' as UpdaterType,
        verified: false,
        notes: newMetricData.notes,
      });
      addNotification({
        type: 'record_added',
        title: 'Health record added',
        message: `${newMetricData.type} (${newMetricData.value} ${newMetricData.unit}) was recorded for ${petName}.`,
        petId,
        petName,
        icon: '📏',
        source: 'E-Passport',
      });
      setShowAddModal(false);
    } catch (err) {
      console.error('Failed to add metric:', err);
      alert(
        t(
          'features.healthRecords.errors.addMetricFailed',
          'Aggiunta metrica non riuscita. Riprova.'
        )
      );
    }
  };

  // Format transaction signature for display
  const formatTxSignature = (sig?: string) => {
    if (!sig) return null;
    return sig.substring(0, 8) + '...' + sig.substring(sig.length - 4);
  };

  // Only show loading spinner on initial load (no cached data)
  if (isLoading && metrics.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        <span className="ml-3 text-gray-600 dark:text-white/70">
          {t('features.healthRecords.loadingPhysicalMetrics')}
        </span>
      </div>
    );
  }

  if (error && metrics.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400 mb-4">
          {t('features.healthRecords.failedToLoadPhysicalMetrics')}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          {t('features.healthRecords.tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3
            className="text-xl mb-1 text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {t('features.healthRecords.physicalMetrics')}
          </h3>
          <p
            className="text-sm text-gray-600 dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.healthRecords.physicalMetricsDesc')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isFetching && (
            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
          )}
          <button
            onClick={() => setShowAddModal(true)}
            disabled={createMetricMutation.isPending}
            className="flex items-center gap-2 bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
          >
            {createMetricMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {t('features.healthRecords.addMetric')}
          </button>
        </div>
      </div>

      {/* Empty state */}
      {metrics.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-[#0F0E16] rounded-lg border border-gray-200 dark:border-white/8">
          <p
            className="text-gray-500 dark:text-white/50 mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.healthRecords.noPhysicalMetricsRecorded')}
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="text-blue-600 dark:text-blue-400 hover:underline"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('features.healthRecords.addFirstMetric')}
          </button>
        </div>
      )}

      {/* Current Metrics Cards */}
      {metrics.length > 0 && (
        <div className="grid md:grid-cols-3 gap-4">
          {/* Weight */}
          {latestWeight && (
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div
                  className="text-sm text-gray-600 dark:text-white/50"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Current Weight
                </div>
                {weightChange !== 0 && (
                  <div
                    className={`flex items-center gap-1 text-sm ${weightChange > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {weightChange > 0 ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}
                    {Math.abs(weightChange).toFixed(1)} {latestWeight.unit}
                  </div>
                )}
              </div>
              <div
                className="text-3xl mb-2 text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {latestWeight.value}{' '}
                <span
                  className="text-lg text-gray-500 dark:text-white/50"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {latestWeight.unit}
                </span>
              </div>
              <div
                className="text-xs text-gray-500 dark:text-white/50 mb-3"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Last updated: {new Date(latestWeight.date).toLocaleDateString()}
              </div>
              {getUpdaterBadge(latestWeight)}
            </div>
          )}

          {/* Height */}
          {latestHeight && (
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-6">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-4"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Height / Length
              </div>
              <div
                className="text-3xl mb-2 text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {latestHeight.value}{' '}
                <span
                  className="text-lg text-gray-500 dark:text-white/50"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {latestHeight.unit}
                </span>
              </div>
              <div
                className="text-xs text-gray-500 dark:text-white/50 mb-3"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Last updated: {new Date(latestHeight.date).toLocaleDateString()}
              </div>
              {getUpdaterBadge(latestHeight)}
            </div>
          )}

          {/* Body Condition Score */}
          {latestBodyCondition && (
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-6">
              <div
                className="text-sm text-gray-600 dark:text-white/50 mb-4"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Body Condition Score
              </div>
              <div
                className="text-3xl mb-2 text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {latestBodyCondition.value}
                <span
                  className="text-lg text-gray-500 dark:text-white/50"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {latestBodyCondition.unit}
                </span>
              </div>
              <div
                className="text-xs text-gray-500 dark:text-white/50 mb-3"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Last updated:{' '}
                {new Date(latestBodyCondition.date).toLocaleDateString()}
              </div>
              {getUpdaterBadge(latestBodyCondition)}
            </div>
          )}
        </div>
      )}

      {/* Weight History Chart */}
      {weightHistory.length > 0 &&
        (() => {
          // Calculate dynamic max for progress bar scaling (add 20% padding)
          const maxWeight = Math.max(...weightHistory.map((m) => m.value));
          const scaleMax = maxWeight * 1.2;

          return (
            <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/8 rounded-lg p-6">
              <h4
                className="mb-4 text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                Weight History
              </h4>
              <div className="space-y-3">
                {weightHistory.map((metric) => {
                  const percentage = Math.min(
                    (metric.value / scaleMax) * 100,
                    100
                  );
                  return (
                    <div key={metric.id} className="flex items-center gap-4">
                      <div
                        className="w-32 text-sm text-gray-600 dark:text-white/70"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {new Date(metric.date).toLocaleDateString()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-gray-200 dark:bg-white/10 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <div
                            className="w-20 text-right text-gray-900 dark:text-white"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {metric.value} {metric.unit}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getUpdaterBadge(metric)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

      {/* Smart Contract Audit Trail */}
      {metrics.length > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-500/30 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <h4
              className="text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              Smart Contract Audit Trail
            </h4>
          </div>
          <p
            className="text-sm text-gray-700 dark:text-purple-100 mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            All updates are written to a mutable smart contract linked to your
            pet&apos;s DID. Historical data is preserved - no overwrites, only
            appends.
          </p>
          <div className="space-y-2">
            {metrics.slice(0, 5).map((metric) => (
              <div
                key={metric.id}
                className="flex items-center gap-3 text-sm bg-white/50 dark:bg-white/5 rounded p-2"
              >
                <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                <span
                  className="text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {new Date(metric.createdAt).toLocaleString()}
                </span>
                <span
                  className="flex-1 text-gray-700 dark:text-white/80"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {metric.type === 'weight'
                    ? 'Weight'
                    : metric.type === 'height'
                      ? 'Height'
                      : 'Body Condition'}{' '}
                  updated to {metric.value}
                  {metric.unit}
                </span>
                {metric.txSignature ? (
                  <a
                    href={`https://explorer.solana.com/tx/${metric.txSignature}${getSolanaClusterParam()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 hover:underline"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    TX: {formatTxSignature(metric.txSignature)}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span
                    className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <Clock className="w-3 h-3" />
                    Awaiting blockchain sync
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Note */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
        <h4
          className="mb-2 text-gray-900 dark:text-white"
          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
        >
          {t('features.healthRecords.whyPhysicalMetricsMatters', {
            defaultValue: 'Why physical metrics matter',
          })}
        </h4>
        <p
          className="text-sm text-gray-700 dark:text-blue-100"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {t('features.healthRecords.whyPhysicalMetricsBody', {
            defaultValue:
              'Tracking weight, height, and body condition helps identify trends early and supports better decisions with your vet.',
          })}
        </p>
      </div>

      {showAddModal && (
        <AddMetricModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddMetric}
        />
      )}
    </div>
  );
}
